import * as monaco from "monaco-editor";
import { BigNumber } from "bignumber.js";
import { action, computed, observable, reaction, runInAction } from "mobx";
import { Status } from "../constants";
import {
  BigqueryDryrunResult,
  BigqueryQuery,
  BigquerySavedQuery,
  BigquerySharedQuery,
  ChatHistoriesTitlesBigqueryQueryResponse200,
  CreateTitlesBigqueryQueryResponse400,
  FixTitlesBigqueryQueryResponse200,
  ShowTitlesBigqueryChatPreferenceResponse200,
} from "lib/Api";
import { call } from "lib/apiHelper";
import { QueryApi as Api, SharedQueryApi, SavedQueryApi } from "lib/Api";
import { defer } from "lib/defer";
import { notificationStore } from "lib/store";
import { arrayToCsv, csvToArray, expandJsonInCsv } from "../lib/Converter";
import { LocalStorage, LocalStorageKey } from "lib/LocalStorage";
import { debounce } from "lib/debounce";
import { Item, TreeItemIconType } from "../components/TreeView/types";
import I18n from "lib/I18n";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import {
  ChatMessage,
  FunctionCallEvent,
  hideUserInstructions,
  Role,
  ToolKind,
  ToolUse,
} from "./chat";
import { references, summaries } from "../lib/bigqueryFunctionReference";
import { PyodideManager, PyodideSession } from "../lib/pyodide";
import { calcDiff } from "../lib/diff";
import {
  configureBigqueryKeywordCase,
  configureBigqueryLanguageProvider,
} from "../lib/bigqueryLanguageProvider";

let titleBeforeQueryNotification: string | undefined;

export const QUERY_NOTIFICATION_CLICK_EVENT =
  "bigquery-query-notification-click";

export interface QueryNotificationClickDetail {
  queryId: string;
}

function requestQueryNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    void Notification.requestPermission().catch(() => undefined);
  }
}

function notifyQueryCompletion(
  queryId: string,
  status: Status.Success | Status.Error,
  message: string,
) {
  if (document.visibilityState !== "hidden") {
    return;
  }

  const statusKey =
    status === Status.Success
      ? "bigquery_query_viewer.label.status_success"
      : "bigquery_query_viewer.label.status_error";
  const messageKey =
    status === Status.Success
      ? "bigquery_query_viewer.messages.status_success"
      : "bigquery_query_viewer.messages.status_error";
  const title = I18n.t(statusKey);
  const body = message || I18n.t(messageKey);

  if (titleBeforeQueryNotification === undefined) {
    titleBeforeQueryNotification = document.title;
    const restoreTitle = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      document.title = titleBeforeQueryNotification ?? document.title;
      titleBeforeQueryNotification = undefined;
      document.removeEventListener("visibilitychange", restoreTitle);
    };
    document.addEventListener("visibilitychange", restoreTitle);
  }
  document.title = `${title} - ${titleBeforeQueryNotification}`;

  if ("Notification" in window && Notification.permission === "granted") {
    const notification = new Notification(title, {
      body: `${body}\n${queryId}`,
    });
    notification.onclick = () => {
      window.focus();
      window.dispatchEvent(
        new CustomEvent<QueryNotificationClickDetail>(
          QUERY_NOTIFICATION_CLICK_EVENT,
          { detail: { queryId } },
        ),
      );
      notification.close();
    };
  }
}

export type Dataset = {
  name: string;
  tables?: Table[];
  funcs?: Func[];
  default?: boolean;
};

export type Table = {
  name: string;
  description: string;
  fields: Field[];
};

export type Field = {
  name: string;
  type: string;
  repeated: boolean;
  description: string;
  fields?: Field[];
};

export type Func = {
  name: string;
  content: string;
  example: string;
  description: string;
  signature?: {
    args: { name?: string; type?: string }[];
    ret?: string;
  };
};

export type InitialProps = {
  titleUuid: string;
  env: string;
  projectId: string;
  useBigquery: boolean;
  datasets: Dataset[];
  limits: {
    resultRowLimit: number;
    recentQueryLimit: number;
    quotaPerDayPerUser: number;
  };
};

let store: Store | undefined;

export function getStore() {
  if (!store) {
    throw new Error("Store not initialized");
  }
  return store;
}

export function createStore(props: InitialProps) {
  configureBigqueryLanguageProvider(props.projectId, props.datasets);
  store = new Store(props);
}

class EventEmitter<T> {
  callbacks: ((value: T) => void)[] = [];

  on(listener: (value: T) => void) {
    this.callbacks.push(listener);
  }

  off(listener: (value: T) => void) {
    const index = this.callbacks.indexOf(listener);
    if (index >= 0) {
      this.callbacks.splice(index, 1);
    }
  }

  emit(value: T) {
    for (const callback of this.callbacks) {
      callback(value);
    }
  }
}

class Store {
  app: AppStore;
  @observable accessor forms: FormStore[] = [];
  @observable accessor graphForms: GraphFormStore[] = [];
  list: ListStore;
  explorer: ExplorerStore;
  chat: ChatStore;
  preferences = new Preferences();
  readonly formAdded = new EventEmitter<FormStore>();
  readonly formRemoved = new EventEmitter<FormStore>();
  readonly graphFormAdded = new EventEmitter<GraphFormStore>();
  readonly graphFormRemoved = new EventEmitter<GraphFormStore>();

  constructor(props: InitialProps) {
    this.app = new AppStore(this, props);
    this.list = new ListStore(this);
    this.chat = new ChatStore(this);
    this.explorer = new ExplorerStore(this, props.datasets);
    this.createFormStore();
  }

  public createFormStore(): FormStore {
    for (let i = this.forms.length + 1; ; i++) {
      const title = I18n.t("bigquery_query_viewer.label.query_n", {
        n: i,
      });
      if (this.forms.some((_) => _.title == title)) {
        continue;
      }
      const formStore = new FormStore(this, title);
      this.addForm(formStore);
      return formStore;
    }
  }

  public createFormStoreFromSavedQuery(
    savedQuery: BigquerySavedQuery,
  ): FormStore {
    const formStore = new FormStore(this, savedQuery.name, savedQuery);
    this.addForm(formStore);
    return formStore;
  }

  public createFormStoreFromSharedQuery(
    sharedQuery: BigquerySharedQuery,
  ): FormStore {
    const formStore = new FormStore(
      this,
      sharedQuery.name,
      undefined,
      sharedQuery,
    );
    this.addForm(formStore);
    return formStore;
  }

  public createFormStoreFromQueryResult(queryResult: QueryResult): FormStore {
    const formStore = new FormStore(this, queryResult.queryId);
    formStore.queryResult = queryResult;
    formStore.query = queryResult.query;
    this.addForm(formStore);
    return formStore;
  }

  private addForm(form: FormStore) {
    this.forms.push(form);
    this.formAdded.emit(form);
  }

  public removeForm(form: FormStore) {
    const index = this.forms.indexOf(form);
    if (index >= 0) {
      this.forms.splice(index, 1);
      this.formRemoved.emit(form);
    }
  }

  public createGraphFormStoreFromQueryResult(
    queryResult: QueryResult,
  ): GraphFormStore {
    const graphFormStore = new GraphFormStore(this, queryResult);
    this.addGraphForm(graphFormStore);
    return graphFormStore;
  }

  private addGraphForm(graphForm: GraphFormStore) {
    this.graphForms.push(graphForm);
    this.graphFormAdded.emit(graphForm);
  }

  public removeGraphForm(graphForm: GraphFormStore) {
    const index = this.graphForms.indexOf(graphForm);
    if (index >= 0) {
      this.graphForms.splice(index, 1);
      this.graphFormRemoved.emit(graphForm);
    }
  }
}

class StoreBase {
  protected parent: Store;

  constructor(parent: Store) {
    this.parent = parent;
  }
}

class AppStore extends StoreBase {
  readonly titleUuid: string;
  readonly env: string;
  readonly useBigquery: boolean;
  readonly limits: InitialProps["limits"];

  constructor(parent: Store, props: InitialProps) {
    super(parent);
    this.titleUuid = props.titleUuid;
    this.env = props.env;
    this.useBigquery = props.useBigquery;
    this.limits = props.limits;
  }
}

export type FixEdits = FixTitlesBigqueryQueryResponse200["edits"];

export class FormStore extends StoreBase {
  @observable accessor title = "";
  @observable accessor query = "";
  @observable accessor nesBaseQuery = "";
  @observable accessor status = Status.WaitingEnqueue;
  @observable accessor executeQueryError:
    CreateTitlesBigqueryQueryResponse400 | undefined;
  @observable accessor dryrunStatus = false;
  @observable accessor dryrunResult: BigqueryDryrunResult | undefined =
    undefined;
  @observable accessor confirming = false;
  @observable accessor isQueryCompleting = false;
  @observable accessor isQueryFixing = false;
  @observable accessor isQueryNesing = false;
  @observable accessor fixEdits: FixEdits = [];
  @observable accessor nesEdits: FixEdits = [];
  @observable accessor prompt = "";
  @observable accessor editDraft = "";
  @observable accessor isQueryEditing = false;
  @observable accessor isUserQueryEditing = false;
  @observable accessor isModified = false;
  @observable accessor queryResult: QueryResult | undefined = undefined;

  readonly savedQuery: BigquerySavedQuery | undefined;
  readonly sharedQuery: BigquerySharedQuery | undefined;

  public id: string;
  public skipNextNesQuery = false;
  private fixAbortController: AbortController | undefined;
  private nesAbortController: AbortController | undefined;
  private dryrunRequestId = 0;

  constructor(
    parent: Store,
    title: string,
    savedQuery?: BigquerySavedQuery,
    sharedQuery?: BigquerySharedQuery,
  ) {
    super(parent);

    this.title = title;
    this.savedQuery = savedQuery;
    this.sharedQuery = sharedQuery;
    if (savedQuery) {
      this.id = `saved_query/${savedQuery.id}`;
      this.query = savedQuery.query;
      reaction(
        () => this.savedQuery?.name,
        () => (this.title = this.savedQuery?.name ?? ""),
      );
      this.dryrunQuery();
    } else if (sharedQuery) {
      this.id = `saved_query/${sharedQuery.id}`;
      this.query = sharedQuery.query;
      reaction(
        () => this.sharedQuery?.name,
        () => (this.title = this.sharedQuery?.name ?? ""),
      );
      this.dryrunQuery();
    } else {
      this.id = title;
    }

    reaction(
      () => this.query,
      () => {
        this.isModified = true;
        if (this.fixAbortController) {
          this.fixAbortController.abort();
        }
        if (this.nesAbortController) {
          this.nesAbortController.abort();
        }
      },
    );
    reaction(
      () => this.query,
      debounce(() => {
        if (this.query.trim() === "") {
          this.clearDryrunResult();
          return;
        }
        if (!this.dryrunStatus) {
          this.dryrunQuery();
        }
        if (this.skipNextNesQuery) {
          this.skipNextNesQuery = false;
          return;
        }
        if (!this.isQueryNesing) {
          this.nesQuery();
        }
      }, 1000),
    );
  }

  @action save() {
    this.isModified = false;
  }

  @action finishFixEdits() {
    this.fixEdits = [];
    this.nesEdits = [];
    this.nesBaseQuery = this.query;
  }

  @action async executeQuery(returnResponse = false) {
    requestQueryNotificationPermission();
    this.status = Status.Waiting;
    this.executeQueryError = undefined;
    using _ = defer(() => (this.status = Status.WaitingEnqueue));
    const response = await call(
      Api.createTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        { query: this.query },
      ),
    );
    switch (response.code) {
      case 200:
        notificationStore.notifySuccess(response.body.message);
        const queryResult = this.parent.list.addQueryResult(
          this.query,
          response.body.queryId,
        );
        this.queryResult = queryResult;
        if (!this.savedQuery && !this.sharedQuery) {
          this.title = queryResult.queryId;
          this.id = queryResult.queryId;
        }
        return response;
      case 400:
        this.executeQueryError = response.body;
        notificationStore.notifyError(response.body.message);
        if (returnResponse) {
          return response;
        }
        break;
    }
  }

  @action async completeQuery(query: string) {
    this.isQueryCompleting = true;
    using _ = defer(() => {
      this.isQueryCompleting = false;
    });
    const response = await call(
      Api.completeTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        { query: query },
      ),
    );
    switch (response.code) {
      case 200:
        return response.body.completion;
    }
  }

  @action async dryrunQuery() {
    if ((this.isUserQueryEditing ? this.editDraft : this.query).trim() === "") {
      this.clearDryrunResult();
      return;
    }
    const requestId = ++this.dryrunRequestId;
    this.dryrunStatus = true;
    this.dryrunResult = undefined;
    this.executeQueryError = undefined;
    using _ = defer(() => {
      if (requestId === this.dryrunRequestId) {
        this.dryrunStatus = false;
      }
    });
    const response = await call(
      Api.dryrunTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        { query: this.isUserQueryEditing ? this.editDraft : this.query },
      ),
      [400],
    );
    if (requestId !== this.dryrunRequestId) {
      return;
    }
    switch (response.code) {
      case 200:
        this.dryrunResult = response.body;
        return true;
      case 400:
        this.executeQueryError = response.body;
        this.debouncedFixQuery();
        break;
    }
  }

  @action private clearDryrunResult() {
    this.dryrunRequestId++;
    this.dryrunStatus = false;
    this.dryrunResult = undefined;
    this.executeQueryError = undefined;
  }

  @action async nesQuery() {
    if (this.fixEdits.length > 0 || this.isQueryNesing) {
      return;
    }
    if (
      this.executeQueryError ||
      !this.parent.preferences.bigqueryAICompletionEnabled
    ) {
      return;
    }
    const diff = calcDiff(
      this.nesBaseQuery.replaceAll("\r\n", "\n").split("\n"),
      this.query.replaceAll("\r\n", "\n").split("\n"),
    );
    if (!diff) {
      this.nesBaseQuery = this.query;
      return;
    }
    this.isQueryNesing = true;
    using _ = defer(() => (this.isQueryNesing = false));
    const abortController = new AbortController();
    this.nesAbortController = abortController;
    const response = await call(
      Api.nesTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        { query: diff.content, oldText: diff.oldText, newText: diff.newText },
      ),
      [400],
    );
    switch (response.code) {
      case 200:
        if (abortController.signal.aborted) {
          this.nesBaseQuery = this.query;
          return;
        }
        this.nesEdits = response.body.edits;
        if (this.nesEdits.length == 0) {
          this.nesBaseQuery = this.query;
        }
        break;
    }
  }

  private debouncedFixQuery = debounce(this.fixQuery.bind(this), 1500);

  @action async fixQuery() {
    if (this.fixEdits.length > 0) {
      return;
    }
    if (
      !this.executeQueryError ||
      !this.parent.preferences.bigqueryAICompletionEnabled
    ) {
      return;
    }
    this.isQueryFixing = true;
    using _ = defer(() => (this.isQueryFixing = false));
    const abortController = new AbortController();
    this.fixAbortController = abortController;
    const response = await call(
      Api.fixTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        {
          query: this.query,
          diagnostics: [
            {
              message: this.executeQueryError.message,
              lineNumber: this.executeQueryError.line,
              column: this.executeQueryError.column,
            },
          ],
        },
      ),
    );
    switch (response.code) {
      case 200:
        if (abortController.signal.aborted) {
          return;
        }
        this.fixEdits = response.body.edits;
    }
  }

  @action async editQuery() {
    if (!this.parent.preferences.bigqueryAICompletionEnabled) {
      return;
    }
    if (!this.isUserQueryEditing) {
      this.editDraft = this.query;
    }
    this.isQueryEditing = true;
    using _ = defer(() => (this.isQueryEditing = false));
    const response = await call(
      Api.editTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        {
          query: this.editDraft,
          prompt: this.prompt,
        },
      ),
    );
    switch (response.code) {
      case 200:
        if (!response.body.query) {
          return;
        }
        if (response.body.query != this.editDraft) {
          this.editDraft = response.body.query;
          this.isUserQueryEditing = true;
        }
    }
  }

  @action cancelEditQuery() {
    this.isUserQueryEditing = false;
  }

  @action acceptEditQuery() {
    this.isUserQueryEditing = false;
    this.query = this.editDraft;
  }

  @action async updateSavedQuery() {
    if (!this.savedQuery) {
      return;
    }
    const response = await call(
      SavedQueryApi.updateTitlesBigquerySavedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        this.savedQuery.id,
        {
          name: this.savedQuery.name,
          query: this.query,
        },
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.savedQuery.query = this.query;
    this.save();
  }

  @action async updateSharedQuery() {
    if (!this.sharedQuery || !this.sharedQuery.updatable) {
      return;
    }
    const response = await call(
      SharedQueryApi.updateTitlesBigquerySharedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        this.sharedQuery.id,
        {
          name: this.sharedQuery.name,
          query: this.query,
        },
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.sharedQuery.query = this.query;
    this.save();
    return;
  }

  get preferences() {
    return this.parent.preferences;
  }

  @computed get markers() {
    return this.executeQueryError
      ? [
          {
            severity: monaco.MarkerSeverity.Error,
            startLineNumber: this.executeQueryError.line,
            startColumn: this.executeQueryError.columnRange[0] + 1,
            endLineNumber: this.executeQueryError.line,
            endColumn: this.executeQueryError.columnRange[1] + 2,
            message: this.executeQueryError.message,
          },
        ]
      : [];
  }
}

interface PreferencesInterface {
  showReservedWordsAsLowerCase: boolean;
  bigqueryUserDefinedThresholdEnabled: boolean;
  // GB
  bigqueryUserDefinedThreshold: number;
  bigqueryAICompletionEnabled: boolean;
  bigqueryAICompletionToSConsent: boolean;
}

class Preferences implements PreferencesInterface {
  @observable accessor showReservedWordsAsLowerCase = false;
  @observable accessor bigqueryUserDefinedThresholdEnabled = false;
  @observable accessor bigqueryUserDefinedThreshold = 100; // GB
  @observable accessor bigqueryAICompletionEnabled = false;
  @observable accessor bigqueryAICompletionToSConsent = false;

  constructor() {
    reaction(
      () => this.showReservedWordsAsLowerCase,
      (lowerCase) => configureBigqueryKeywordCase(lowerCase),
    );
    reaction(
      () => [
        this.showReservedWordsAsLowerCase,
        this.bigqueryUserDefinedThresholdEnabled,
        this.bigqueryUserDefinedThreshold,
        this.bigqueryAICompletionEnabled,
        this.bigqueryAICompletionToSConsent,
      ],
      () => this.save(),
    );
  }

  @action load() {
    const preferences = LocalStorage.load(
      LocalStorageKey.QueryEditorPreferences,
    ) as PreferencesInterface;
    if (preferences.showReservedWordsAsLowerCase != null) {
      this.showReservedWordsAsLowerCase =
        preferences.showReservedWordsAsLowerCase;
    }
    if (preferences.bigqueryUserDefinedThresholdEnabled != null) {
      this.bigqueryUserDefinedThresholdEnabled =
        preferences.bigqueryUserDefinedThresholdEnabled;
    }
    if (preferences.bigqueryUserDefinedThreshold != null) {
      this.bigqueryUserDefinedThreshold =
        preferences.bigqueryUserDefinedThreshold;
    }
    if (preferences.bigqueryAICompletionEnabled != null) {
      this.bigqueryAICompletionEnabled =
        preferences.bigqueryAICompletionEnabled;
    }
    if (preferences.bigqueryAICompletionToSConsent != null) {
      this.bigqueryAICompletionToSConsent =
        preferences.bigqueryAICompletionToSConsent;
    }
  }

  save() {
    LocalStorage.save(LocalStorageKey.QueryEditorPreferences, {
      showReservedWordsAsLowerCase: this.showReservedWordsAsLowerCase,
      bigqueryUserDefinedThresholdEnabled:
        this.bigqueryUserDefinedThresholdEnabled,
      bigqueryUserDefinedThreshold: this.bigqueryUserDefinedThreshold,
      bigqueryAICompletionEnabled: this.bigqueryAICompletionEnabled,
      bigqueryAICompletionToSConsent: this.bigqueryAICompletionToSConsent,
    } satisfies PreferencesInterface);
  }
}

class ListStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor queryResults: QueryResult[] = [];

  @action async listQueryResults() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const response = await call(
      Api.indexTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
      ),
    );
    if (response.code != 200) {
      return;
    }
    const previous = this.queryResults;
    const next = response.body.map((queryResult) => {
      const existing = previous.find((_) => _.queryId === queryResult.queryId);
      return existing ?? QueryResult.fromResponse(queryResult);
    });
    previous
      .filter((queryResult) => !next.includes(queryResult))
      .forEach((queryResult) => queryResult.stopPolling());
    this.queryResults = next;
  }

  @action addQueryResult(query: string, queryId: string): QueryResult {
    const queryResult = new QueryResult(query, queryId);
    queryResult.queryStatus = Status.Waiting;
    this.queryResults.unshift(queryResult);
    queryResult.startPolling(this.parent.app.titleUuid, this.parent.app.env);
    return queryResult;
  }

  @action changeQueryStatus(queryId: string, status: Status, message: string) {
    const queryResult = this.queryResults.find(
      (queryResult) => queryResult.queryId === queryId,
    );
    if (!queryResult) {
      return;
    }
    queryResult.changeStatus(status, message);
    if (
      status === Status.Success ||
      status === Status.Error ||
      status === Status.Stopped
    ) {
      queryResult.stopPolling();
    }
    if (status === Status.Success) {
      queryResult.getQuery(this.parent.app.titleUuid, this.parent.app.env);
    }
  }

  @action changeExportStatus(
    queryId: string,
    status: Status,
    message: string,
    signedUrl: string,
  ) {
    const queryResult = this.queryResults.find(
      (queryResult) => queryResult.queryId === queryId,
    );
    if (!queryResult) {
      return;
    }
    queryResult.exportStatus = status;
    queryResult.exportMessage = message;
    switch (status) {
      case Status.Success:
        window.open(signedUrl, "_blank");
        queryResult.exportStatus = Status.Success;
        break;
      case Status.Error:
        queryResult.exportStatus = Status.WaitingEnqueue;
        break;
    }
  }
}

class ExplorerStore extends StoreBase {
  @observable accessor sharedQuery: SharedQueryStore;
  @observable accessor savedQuery: SavedQueryStore;
  @observable accessor expandedIds: string[] = ["saved_query", "shared_query"];
  @observable accessor items: Item[] = [
    {
      label: I18n.t("bigquery_query_viewer.label.saved_query"),
      id: "saved_query",
      isRenaming: false,
      icon: TreeItemIconType.Folder,
      items: [],
    },
    {
      label: I18n.t("bigquery_query_viewer.label.shared_query"),
      id: "shared_query",
      isRenaming: false,
      icon: TreeItemIconType.Folder,
      items: [],
    },
  ];

  readonly datasets: Dataset[];

  constructor(parent: Store, datasets: Dataset[]) {
    super(parent);

    this.datasets = datasets;

    this.sharedQuery = new SharedQueryStore(this.parent);
    this.savedQuery = new SavedQueryStore(this.parent);

    datasets.sort((x, y) => x.name.localeCompare(y.name));
    this.items.push(...datasets.map((_) => this.datasetToItem(_)));
    this.expandedIds.push(
      ...datasets.filter((_) => _.default).map((_) => _.name),
    );
  }

  @action public async list() {
    await Promise.all([this.sharedQuery.list(), this.savedQuery.list()]);
    this.updateSavedQueryTreeItem();
    this.updateSharedQueryTreeItem();
  }

  @action public async renameSavedQuery(
    savedQuery: BigquerySavedQuery,
    newName: string,
  ) {
    const response = await call(
      SavedQueryApi.updateTitlesBigquerySavedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        savedQuery.id,
        { name: newName, query: savedQuery.query },
      ),
    );
    if (response.code != 200) {
      return;
    }
    savedQuery.name = newName;
    return true;
  }

  @action public async renameSharedQuery(
    sharedQuery: BigquerySharedQuery,
    newName: string,
  ) {
    const response = await call(
      SharedQueryApi.updateTitlesBigquerySharedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        sharedQuery.id,
        { name: newName, query: sharedQuery.query },
      ),
    );
    if (response.code != 200) {
      return;
    }
    sharedQuery.name = newName;
    return true;
  }

  private updateSavedQueryTreeItem() {
    this.items[0].items = this.savedQuery.savedQueries.map((_) => ({
      label: _.name,
      id: `saved_query/${_.id}`,
      isRenaming: false,
      icon: TreeItemIconType.FileSql,
    }));
  }

  private updateSharedQueryTreeItem() {
    this.items[1].items = this.sharedQuery.sharedQueries.map((_) => ({
      label: _.name,
      labelHelper: `(${I18n.t("bigquery_query_viewer.label.shared_query_creator")}: ${_.userName})`,
      id: `shared_query/${_.id}`,
      isRenaming: false,
      icon: TreeItemIconType.FileSql,
    }));
  }

  private datasetToItem(dataset: Dataset): Item {
    return {
      label: dataset.name,
      id: dataset.name,
      isRenaming: false,
      items: (dataset.tables ?? [])
        .map((_) => this.tableToItem(dataset, _))
        .concat((dataset.funcs ?? []).map((_) => this.funcToItem(dataset, _))),
      icon: TreeItemIconType.Database,
    };
  }

  private tableToItem(dataset: Dataset, table: Table): Item {
    return {
      label: table.name,
      id: `${dataset.name}/${table.name}`,
      isRenaming: false,
      icon: TreeItemIconType.Table,
    };
  }

  private funcToItem(dataset: Dataset, func: Func): Item {
    return {
      label: func.name,
      id: `${dataset.name}/${func.name}`,
      isRenaming: false,
      icon: TreeItemIconType.FileEarmarkCode,
    };
  }
}

class SharedQueryStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor sharedQueries: BigquerySharedQuery[] = [];
  @observable accessor isCreateOpen = false;
  @observable accessor name = "";
  @observable accessor form: FormStore | undefined = undefined;
  @observable accessor isCreating = false;
  @observable accessor isDeleteOpen = false;
  @observable accessor selectedSharedQuery: BigquerySharedQuery | undefined =
    undefined;
  @observable accessor isDeleting = false;

  @action async list() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const response = await call(
      SharedQueryApi.indexTitlesBigquerySharedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.sharedQueries = response.body;
  }

  @action async create() {
    if (!this.form) {
      return;
    }
    this.isCreating = true;
    using _ = defer(() => (this.isCreating = false));
    const response = await call(
      SharedQueryApi.createTitlesBigquerySharedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        { name: this.name, query: this.form.query },
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.isCreateOpen = false;
    await this.list();
    this.parent.explorer.list();
  }

  @action async delete() {
    if (!this.selectedSharedQuery) {
      return;
    }
    this.isDeleting = true;
    using _ = defer(() => (this.isDeleting = false));
    const response = await call(
      SharedQueryApi.destroyTitlesBigquerySharedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        this.selectedSharedQuery.id,
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.isDeleteOpen = false;
    await this.list();
    this.parent.explorer.list();
    return true;
  }
}

class SavedQueryStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor savedQueries: BigquerySavedQuery[] = [];
  @observable accessor isCreateOpen = false;
  @observable accessor name = "";
  @observable accessor form: FormStore | undefined = undefined;
  @observable accessor isCreating = false;
  @observable accessor isDeleteOpen = false;
  @observable accessor selectedSavedQuery: BigquerySavedQuery | undefined =
    undefined;
  @observable accessor isDeleting = false;

  @action async list() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const response = await call(
      SavedQueryApi.indexTitlesBigquerySavedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.savedQueries = response.body;
  }

  @action async create() {
    if (!this.form) {
      return;
    }
    this.isCreating = true;
    using _ = defer(() => (this.isCreating = false));
    const response = await call(
      SavedQueryApi.createTitlesBigquerySavedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        { name: this.name, query: this.form.query },
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.isCreateOpen = false;
    this.form.save();
    await this.list();
    this.parent.explorer.list();
  }

  @action async delete() {
    if (!this.selectedSavedQuery) {
      return;
    }
    this.isDeleting = true;
    using _ = defer(() => (this.isDeleting = false));
    const response = await call(
      SavedQueryApi.destroyTitlesBigquerySavedQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        this.selectedSavedQuery.id,
      ),
    );
    if (response.code != 200) {
      return;
    }
    this.isDeleteOpen = false;
    await this.list();
    this.parent.explorer.list();
    return true;
  }
}

export type ChatHistory = ChatHistoriesTitlesBigqueryQueryResponse200;
export type ChatPreferences = ShowTitlesBigqueryChatPreferenceResponse200;

class ChatStore extends StoreBase {
  private static readonly QUERY_POLLING_INTERVAL = 20_000;
  @observable accessor prompt = "";
  @observable accessor messages: ChatMessage[] = [];
  @observable accessor isRequesting = false;
  @observable private accessor waitingQueries: {
    sessionKey: string;
    queryId: string;
    tool: ToolUse;
  }[] = [];
  @observable private accessor isStartingQuery = false;
  @observable private accessor pendingTools: ToolUse[] = [];
  private pendingToolResponses = observable.map<string, unknown>(undefined, {
    deep: false,
  });
  @observable
  accessor chatHistories: ChatHistory[] = [];
  @observable accessor isChatHistoryLoading = false;
  @observable accessor showThoughts = false;
  @observable accessor isPreferencesOpen = false;
  @observable accessor isPreferencesLoading = false;
  @observable accessor isPreferencesSaving = false;
  @observable accessor hasPreferencesError = false;
  @observable accessor hasPreferencesLoadError = false;
  @observable accessor chatPreferences: ChatPreferences = {
    userInstruction: "",
    titleUserInstruction: "",
  };

  @observable private accessor sessionKey = "";
  private turnKey = "";
  private retryTurnKey = "";
  private abortController = new AbortController();
  private requestGeneration = 0;

  @computed get currentSessionKey() {
    return this.sessionKey;
  }

  @computed get hasPendingTools() {
    return this.pendingTools.length > 0;
  }

  @computed private get pendingChoice() {
    return this.pendingTools.find(
      (tool) =>
        tool.toolKind == ToolKind.choices &&
        !this.pendingToolResponses.has(tool.callKey),
    );
  }

  @computed get isPromptDisabled() {
    return this.isRequesting || (this.hasPendingTools && !this.pendingChoice);
  }

  @computed get isSessionSwitchBlocked() {
    return (
      this.isStartingQuery ||
      this.waitingQueries.some((query) => query.sessionKey == this.sessionKey)
    );
  }

  constructor(parent: Store) {
    super(parent);
  }

  @action async openPreferences() {
    this.isPreferencesOpen = true;
    this.isPreferencesLoading = true;
    this.hasPreferencesError = false;
    this.hasPreferencesLoadError = false;
    using _ = defer(() => (this.isPreferencesLoading = false));
    const response = await call(
      Api.showTitlesBigqueryChatPreference(
        this.parent.app.titleUuid,
        this.parent.app.env,
      ),
    );
    if (response.code == 200) {
      this.chatPreferences = response.body;
    } else {
      this.hasPreferencesError = true;
      this.hasPreferencesLoadError = true;
    }
  }

  @action closePreferences() {
    this.isPreferencesOpen = false;
  }

  @action async savePreferences() {
    this.isPreferencesSaving = true;
    this.hasPreferencesError = false;
    using _ = defer(() => (this.isPreferencesSaving = false));
    const response = await call(
      Api.updateTitlesBigqueryChatPreference(
        this.parent.app.titleUuid,
        this.parent.app.env,
        this.chatPreferences,
      ),
    );
    if (response.code == 200) {
      this.chatPreferences = response.body;
      this.closePreferences();
    } else {
      this.hasPreferencesError = true;
    }
  }

  @action revert() {
    if (this.isSessionSwitchBlocked) {
      return;
    }
    this.cancelRequest();
    this.isRequesting = false;
    this.waitingQueries = [];
    this.saveWaitingQueries();
    this.pendingTools = [];
    this.pendingToolResponses.clear();
    const lastUserMessageIndex = this.messages.findLastIndex(
      (_) => _.role == Role.user,
    );
    if (lastUserMessageIndex < 0) {
      this.sessionKey = "";
      this.messages = [];
      this.retryTurnKey = "";
      return;
    }
    this.retryTurnKey = this.messages[lastUserMessageIndex].turnKey;
    this.messages.splice(lastUserMessageIndex);
  }

  @action refresh() {
    if (this.isSessionSwitchBlocked) {
      return;
    }
    this.cancelRequest();
    this.sessionKey = "";
    this.prompt = "";
    this.messages = [];
    this.waitingQueries = [];
    this.pendingTools = [];
    this.pendingToolResponses.clear();
    this.retryTurnKey = "";
  }

  @action async listChatHistories() {
    this.isChatHistoryLoading = true;
    using _ = defer(() => (this.isChatHistoryLoading = false));
    const response = await call(
      Api.chatHistoriesTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
      ),
    );
    switch (response.code) {
      case 200:
        this.chatHistories = response.body;
    }
  }

  @action async restoreSession(sessionKey: string) {
    if (sessionKey == this.sessionKey || this.isSessionSwitchBlocked) {
      return;
    }
    this.cancelRequest();
    this.waitingQueries = [];
    this.isRequesting = false;
    this.sessionKey = sessionKey;
    this.messages = [];
    this.pendingTools = [];
    this.pendingToolResponses.clear();
    this.turnKey = "";
    this.retryTurnKey = "";
    const response = await call(
      Api.chatHistoryTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        sessionKey,
      ),
    );
    if (this.sessionKey != sessionKey) {
      return;
    }
    switch (response.code) {
      case 200:
        this.messages = response.body
          .map((message) => {
            switch (message.role as Role) {
              case Role.user:
                if (!message.text) {
                  return;
                }
                return new ChatMessage(
                  hideUserInstructions(message.text),
                  Role.user,
                  {
                    turnKey: message.turnKey,
                  },
                );
              case Role.model:
                const ret = new ChatMessage(message.text, Role.model, {
                  turnKey: message.turnKey,
                });
                ret.add(message.thoughtsText ?? "", true);
                ret.addToolCalls(message.functionCalls ?? []);
                ret.isCompleted = true;
                return ret;
              case Role.tool:
                const toolMessage = new ChatMessage(message.text, Role.tool, {
                  toolKind: message.toolKind as ToolKind,
                  callKey: message.callKey,
                });
                toolMessage.isCompleted = true;
                return toolMessage;
            }
          })
          .filter((_) => !!_);
        const lastMessage = this.messages.at(-1);
        if (
          lastMessage?.role == Role.model &&
          lastMessage.toolUses.length > 0
        ) {
          this.pendingTools = lastMessage.toolUses;
          this.turnKey = lastMessage.turnKey;
          await this.resumePendingTools(lastMessage);
        }
        break;
    }
  }

  @action send(prompt?: string) {
    if (this.isPromptDisabled) {
      return;
    }
    if (prompt == undefined) {
      prompt = this.prompt;
      this.prompt = "";
    }
    if (!prompt.trim()) {
      return;
    }
    if (this.pendingChoice) {
      this.sendToolResponse(this.pendingChoice, { choice: prompt });
      return;
    }
    this.turnKey = Math.random().toString();
    this.messages.push(
      new ChatMessage(prompt, Role.user, { turnKey: this.turnKey }),
    );
    this.isRequesting = true;
    this.sendImpl(prompt, this.retryTurnKey);
    this.retryTurnKey = "";
  }

  @action retry(message: ChatMessage) {
    if (this.isRequesting) {
      return;
    }
    const index = this.messages.findIndex((_) => _ == message);
    const userMessageIndex = this.messages.findLastIndex(
      (_, i) => _.role == Role.user && i < index,
    );
    if (userMessageIndex < 0) {
      return;
    }
    const userMessage = this.messages[userMessageIndex];
    this.messages = this.messages.slice(0, userMessageIndex);
    this.turnKey = Math.random().toString();
    this.messages.push(
      new ChatMessage(userMessage.processedText, Role.user, {
        turnKey: this.turnKey,
      }),
    );
    this.isRequesting = true;
    this.sendImpl(userMessage.processedText, userMessage.turnKey);
  }

  @action public sendToolResponse(
    tool: ToolUse,
    response: unknown,
    requestGeneration = this.requestGeneration,
  ) {
    if (requestGeneration != this.requestGeneration) {
      return;
    }
    const content =
      typeof response == "string" ? response : JSON.stringify(response);
    this.messages.push(
      new ChatMessage(content, Role.tool, {
        toolKind: tool.toolKind,
        callKey: tool.callKey,
      }),
    );
    this.pendingToolResponses.set(tool.callKey, response);
    if (this.pendingToolResponses.size != this.pendingTools.length) {
      return;
    }
    const functionResponses = this.pendingTools.map((pendingTool) => {
      const pendingResponse = this.pendingToolResponses.get(
        pendingTool.callKey,
      );
      return {
        callKey: pendingTool.callKey,
        name: pendingTool.toolKind,
        response:
          typeof pendingResponse == "object" && pendingResponse != null
            ? pendingResponse
            : { result: pendingResponse },
      };
    });
    this.pendingTools = [];
    this.pendingToolResponses.clear();
    this.isRequesting = true;
    this.sendImpl(undefined, undefined, functionResponses);
  }

  @action private sendImpl(
    prompt?: string,
    retryTurnKey?: string,
    functionResponses?: { callKey: string; name: string; response: unknown }[],
  ) {
    const requestGeneration = ++this.requestGeneration;
    const abortController = new AbortController();
    this.abortController = abortController;
    const message = new ChatMessage("", Role.model);
    this.messages.push(message);
    void fetchEventSource(
      `/api/v1/titles/${this.parent.app.titleUuid}/bigquery/${this.parent.app.env}/queries/chat`,
      {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getRailsCsrfToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt,
          sessionKey: this.sessionKey,
          turnKey: this.turnKey,
          retryTurnKey: retryTurnKey,
          functionResponses,
        }),
        signal: abortController.signal,
        onmessage: (e) => {
          if (requestGeneration != this.requestGeneration) {
            return;
          }
          const data = JSON.parse(e.data);
          switch (e.event) {
            case "session":
              this.sessionKey = data.sessionKey;
              break;
            case "thought_delta":
              message.add(data.text, true);
              break;
            case "text_delta":
              message.add(data.text, false);
              break;
            case "function_calls":
              message.addToolCalls(data.calls as FunctionCallEvent[]);
              this.pendingTools = message.toolUses;
              this.pendingToolResponses.clear();
              break;
            case "reset":
              message.reset();
              this.pendingTools = [];
              this.pendingToolResponses.clear();
              break;
            case "error":
              notificationStore.notifyError(data.message);
              break;
          }
        },
        onclose: async () => {
          if (requestGeneration != this.requestGeneration) {
            return;
          }
          abortController.abort();
          message.isCompleted = true;
          if (await this.postProcessMessage(message, requestGeneration)) {
            if (requestGeneration != this.requestGeneration) {
              return;
            }
            this.isRequesting = false;
          }
        },
        onerror: (error) => {
          throw error;
        },
      },
    ).catch((error: unknown) => {
      if (requestGeneration != this.requestGeneration) {
        return;
      }
      abortController.abort();
      message.isCompleted = true;
      this.isRequesting = false;
      notificationStore.notifyError(
        error instanceof Error && error.message
          ? error.message
          : "Chat connection failed",
      );
    });
  }

  public changeQueryStatus(queryId: string, status: Status, message: string) {
    const waitingQuery = this.waitingQueries.find(
      (_) => _.sessionKey == this.sessionKey && _.queryId == queryId,
    );
    if (!waitingQuery) {
      return;
    }
    switch (status) {
      case Status.Error:
        this.waitingQueries = this.waitingQueries.filter(
          (_) => _.queryId != queryId,
        );
        this.saveWaitingQueries();
        this.sendToolResponse(waitingQuery.tool, {
          error: message,
        });
        return;
      case Status.Stopped:
        this.waitingQueries = this.waitingQueries.filter(
          (_) => _.queryId != queryId,
        );
        this.saveWaitingQueries();
        this.sendToolResponse(waitingQuery.tool, {
          error: message || "query execution was stopped",
        });
        return;
      case Status.Success:
        this.waitingQueries = this.waitingQueries.filter(
          (_) => _.queryId != queryId,
        );
        this.saveWaitingQueries();
        this.sendToolResponse(waitingQuery.tool, {
          file_path: `file://${queryId}`,
          status: "success",
        });
        return;
    }
  }

  // ユーザー入力待ち、またはツールの使用がなければtrueを返す
  private async postProcessMessage(
    message: ChatMessage,
    requestGeneration = this.requestGeneration,
  ) {
    const interactiveTools = new Set([
      ToolKind.choices,
      ToolKind.yesNo,
      ToolKind.okCancel,
    ]);
    const automaticTools = message.toolUses.filter(
      (_) => !interactiveTools.has(_.toolKind),
    );
    if (automaticTools.length > 0) {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => setTimeout(resolve, 100));
      });
    }
    for (const tool of automaticTools) {
      if (requestGeneration != this.requestGeneration) {
        return true;
      }
      await this.executeTool(tool, requestGeneration);
    }
    return (
      message.toolUses.length == 0 ||
      automaticTools.length != message.toolUses.length
    );
  }

  private async executeTool(
    tool: ToolUse,
    requestGeneration = this.requestGeneration,
  ) {
    if (requestGeneration != this.requestGeneration) {
      return;
    }
    switch (tool.toolKind) {
      case ToolKind.dryrunQuery:
        return this.dryrunQueryTool(tool, requestGeneration);
      case ToolKind.runQuery:
        return this.runQueryTool(tool, requestGeneration);
      case ToolKind.listFiles:
        return this.listFilesTool(tool);
      case ToolKind.readFile:
        return this.readFileTool(tool);
      case ToolKind.replaceFile:
        return this.replaceFileTool(tool);
      case ToolKind.completeReplaceFile:
        return this.completeReplaceFileTool(tool);
      case ToolKind.cancelReplaceFile:
        return this.cancelReplaceFileTool(tool);
      case ToolKind.listBigqueryFunctions:
        return this.listBigqueryFunctions(tool);
      case ToolKind.getBigqueryFunction:
        return this.getBigqueryFunction(tool);
      case ToolKind.drawGraph:
        return this.drawGraph(tool, requestGeneration);
      default:
        return this.sendToolResponse(
          tool,
          { error: "unknown function" },
          requestGeneration,
        );
    }
  }

  private async dryrunQueryTool(tool: ToolUse, requestGeneration: number) {
    const dryrunResponse = await this.dryrunQuery(
      tool.parameters.map((_) => _.value).join("\n"),
    );
    if (requestGeneration != this.requestGeneration) {
      return;
    }
    let response: Record<string, unknown> = {};
    switch (dryrunResponse.code) {
      case 200:
        response = {
          data_scanned_in_bytes: dryrunResponse.body.dataScannedInBytes,
          cost: dryrunResponse.body.cost,
        };
        break;
      case 400:
        response = {
          error: dryrunResponse.body.message,
          line: dryrunResponse.body.line,
          column: dryrunResponse.body.column,
        };
        break;
    }
    this.sendToolResponse(tool, response, requestGeneration);
  }

  private async runQueryTool(tool: ToolUse, requestGeneration: number) {
    const query = tool.parameters.map((_) => _.value).join("\n");
    let form: FormStore;
    if (this.parent.forms.length == 1 && this.parent.forms[0].query == "") {
      form = this.parent.forms[0];
    } else {
      form = this.parent.createFormStore();
    }
    form.skipNextNesQuery = true;
    form.query = query;
    this.isStartingQuery = true;
    using _ = defer(() => (this.isStartingQuery = false));
    const result = await form.executeQuery();
    if (requestGeneration != this.requestGeneration) {
      return;
    }
    if (!result) {
      this.sendToolResponse(
        tool,
        { error: "query execution was not started" },
        requestGeneration,
      );
      return;
    }
    switch (result.code) {
      case 200:
        this.waitingQueries.push({
          sessionKey: this.sessionKey,
          queryId: result.body.queryId,
          tool,
        });
        this.saveWaitingQueries();
        break;
      case 400:
        this.sendToolResponse(
          tool,
          {
            error: result.body.message,
            line: result.body.line,
            column: result.body.column,
          },
          requestGeneration,
        );
        break;
    }
  }

  private listFilesTool(tool: ToolUse) {
    const paths = this.parent.forms.map((_) => `file://${_.id}`).join("\n");
    if (paths.length == 0) {
      this.sendToolResponse(tool, { error: "no files found" });
      return;
    }
    this.sendToolResponse(tool, { files: paths.split("\n") });
  }

  private readFileTool(tool: ToolUse) {
    const path = this.getParameterValue(tool, "file_path");
    if (!path) {
      return;
    }
    const form = this.parent.forms.find((_) => `file://${_.id}` == path);
    if (!form) {
      this.sendToolResponse(tool, { error: `${path} does not exist` });
      return;
    }
    this.sendToolResponse(tool, {
      content: (form.isUserQueryEditing
        ? form.editDraft
        : form.query
      ).replaceAll("\r\n", "\n"),
    });
  }

  private replaceFileTool(tool: ToolUse) {
    const path = this.getParameterValue(tool, "file_path");
    if (!path) {
      return;
    }
    const form = this.parent.forms.find((_) => `file://${_.id}` == path);
    if (!form) {
      this.sendToolResponse(tool, { error: `${path} does not exist` });
      return;
    }
    const replacement =
      tool.parameters.find((_) => _.key == "replacement")?.value ?? "";
    const search = this.getParameterValue(tool, "search");
    // クエリが空で、検索文字列も指定されていない場合は置換をそのまま適用
    if (!form.query && !search) {
      form.editDraft = replacement;
      form.isUserQueryEditing = true;
      this.sendToolResponse(tool, { newContent: form.editDraft });
      return;
    }
    if (!search) {
      return;
    }
    const targetQuery = (
      form.isUserQueryEditing ? form.editDraft : form.query
    ).replaceAll("\r\n", "\n");
    if (!targetQuery.includes(search)) {
      this.sendToolResponse(tool, {
        error: "no matched string found for search",
      });
      return;
    }
    form.editDraft = targetQuery.replace(search, replacement);
    form.isUserQueryEditing = true;
    this.sendToolResponse(tool, { newContent: form.editDraft });
  }

  private completeReplaceFileTool(tool: ToolUse) {
    const path = this.getParameterValue(tool, "file_path");
    if (!path) {
      return;
    }
    const form = this.parent.forms.find((_) => `file://${_.id}` == path);
    if (!form) {
      this.sendToolResponse(tool, { error: `${path} does not exist` });
      return;
    }
    if (!form.isUserQueryEditing) {
      this.sendToolResponse(tool, {
        error: `${path} does not in replacing mode`,
      });
      return;
    }
    form.acceptEditQuery();
    this.sendToolResponse(tool, {
      message: `${path} content was successfully replaced.`,
    });
  }

  private cancelReplaceFileTool(tool: ToolUse) {
    const path = this.getParameterValue(tool, "file_path");
    if (!path) {
      return;
    }
    const form = this.parent.forms.find((_) => `file://${_.id}` == path);
    if (!form) {
      this.sendToolResponse(tool, { error: `${path} does not exist` });
      return;
    }
    if (!form.isUserQueryEditing) {
      this.sendToolResponse(tool, {
        error: `${path} does not in replacing mode`,
      });
      return;
    }
    form.cancelEditQuery();
    this.sendToolResponse(tool, {
      message: `Replacing ${path} content was canceled.`,
    });
  }

  private listBigqueryFunctions(tool: ToolUse) {
    this.sendToolResponse(tool, { functions: summaries });
  }

  private getBigqueryFunction(tool: ToolUse) {
    const name = this.getParameterValue(tool, "name");
    if (!name) {
      return;
    }
    if (!references[name]) {
      this.sendToolResponse(tool, {
        error: `${name} is not BigQuery builtin function`,
      });
      return;
    }
    this.sendToolResponse(tool, { reference: references[name] });
  }

  private async drawGraph(tool: ToolUse, requestGeneration: number) {
    const path = this.getParameterValue(tool, "file_path");
    if (!path) {
      return;
    }
    const form = this.parent.forms.find((_) => `file://${_.id}` == path);
    if (!form) {
      this.sendToolResponse(tool, { error: `${path} does not exist` });
      return;
    }
    if (!form.queryResult) {
      this.sendToolResponse(tool, {
        error: `${path} does not have query result.`,
      });
      return;
    }
    const script = this.getParameterValue(tool, "script");
    if (!script) {
      return;
    }
    let graphStore = this.parent.graphForms.find(
      (_) => _.queryResult.queryId == form.queryResult!.queryId,
    );
    if (!graphStore) {
      graphStore = this.parent.createGraphFormStoreFromQueryResult(
        form.queryResult,
      );
    }
    graphStore.script = script;
    await graphStore.run();
    if (requestGeneration != this.requestGeneration) {
      return;
    }
    const markers = graphStore.markers;
    if (markers.length > 0) {
      this.sendToolResponse(
        tool,
        {
          error: markers[0].message,
          line: markers[0].startLineNumber,
        },
        requestGeneration,
      );
      return;
    }
    this.sendToolResponse(tool, { status: "success" }, requestGeneration);
  }

  private getParameterValue(tool: ToolUse, parameter: string): string {
    const param = tool.parameters.find((_) => _.key == parameter);
    if (!param) {
      this.sendToolResponse(tool, {
        error: `${parameter} parameter not found`,
      });
      return "";
    }
    if (!param.value) {
      this.sendToolResponse(tool, {
        error: `${parameter} parameter value is empty`,
      });
      return "";
    }
    return param.value;
  }

  private async resumePendingTools(message: ChatMessage) {
    const requestGeneration = this.requestGeneration;
    const interactiveTools = new Set([
      ToolKind.choices,
      ToolKind.yesNo,
      ToolKind.okCancel,
    ]);
    const restorableTools = new Set([
      ToolKind.dryrunQuery,
      ToolKind.listFiles,
      ToolKind.readFile,
      ToolKind.listBigqueryFunctions,
      ToolKind.getBigqueryFunction,
    ]);
    const storedWaitingQueries = this.loadWaitingQueries().filter((waiting) =>
      message.toolUses.some((tool) => tool.callKey == waiting.tool.callKey),
    );
    this.waitingQueries = storedWaitingQueries;
    this.saveWaitingQueries();
    this.isRequesting = storedWaitingQueries.length > 0;

    for (const tool of message.toolUses) {
      if (interactiveTools.has(tool.toolKind)) {
        continue;
      }
      const waitingQuery = storedWaitingQueries.find(
        (waiting) => waiting.tool.callKey == tool.callKey,
      );
      if (waitingQuery) {
        void this.pollWaitingQuery(waitingQuery);
      } else if (restorableTools.has(tool.toolKind)) {
        await this.executeTool(tool, requestGeneration);
      } else {
        this.sendToolResponse(
          tool,
          { error: "tool execution was interrupted by page reload" },
          requestGeneration,
        );
      }
    }
  }

  private waitingQueriesStorageKey() {
    return `${this.parent.app.titleUuid}/${this.parent.app.env}/${this.sessionKey}`;
  }

  private loadWaitingQueries() {
    const stored = LocalStorage.load(LocalStorageKey.ChatWaitingQueries);
    if (typeof stored != "object" || stored == null || Array.isArray(stored)) {
      return [];
    }
    const waitingQueries = (stored as Record<string, unknown>)[
      this.waitingQueriesStorageKey()
    ];
    return Array.isArray(waitingQueries)
      ? (waitingQueries as { queryId: string; tool: ToolUse }[]).map(
          (waitingQuery) => ({ ...waitingQuery, sessionKey: this.sessionKey }),
        )
      : [];
  }

  private saveWaitingQueries() {
    const stored = LocalStorage.load(LocalStorageKey.ChatWaitingQueries);
    const waitingQueries =
      typeof stored == "object" && stored != null && !Array.isArray(stored)
        ? (stored as Record<string, unknown>)
        : {};
    waitingQueries[this.waitingQueriesStorageKey()] = this.waitingQueries;
    LocalStorage.save(LocalStorageKey.ChatWaitingQueries, waitingQueries);
  }

  private async pollWaitingQuery(waitingQuery: {
    sessionKey: string;
    queryId: string;
    tool: ToolUse;
  }) {
    if (
      waitingQuery.sessionKey != this.sessionKey ||
      !this.waitingQueries.some(
        (query) => query.queryId == waitingQuery.queryId,
      )
    ) {
      return;
    }
    let response: Awaited<ReturnType<typeof Api.showTitlesBigqueryQuery>>;
    try {
      response = await call(
        Api.showTitlesBigqueryQuery(
          this.parent.app.titleUuid,
          this.parent.app.env,
          waitingQuery.queryId,
        ),
      );
    } catch {
      if (
        waitingQuery.sessionKey == this.sessionKey &&
        this.waitingQueries.some(
          (query) => query.queryId == waitingQuery.queryId,
        )
      ) {
        setTimeout(
          () => void this.pollWaitingQuery(waitingQuery),
          ChatStore.QUERY_POLLING_INTERVAL,
        );
      }
      return;
    }
    if (
      waitingQuery.sessionKey != this.sessionKey ||
      !this.waitingQueries.some(
        (query) => query.queryId == waitingQuery.queryId,
      )
    ) {
      return;
    }
    if (response.code == 200) {
      const status = response.body.status as Status;
      if ([Status.Success, Status.Error, Status.Stopped].includes(status)) {
        this.changeQueryStatus(
          waitingQuery.queryId,
          status,
          response.body.message ?? "",
        );
        return;
      }
    } else {
      const message =
        response.code == null
          ? response.body.response.statusText
          : "query status could not be retrieved";
      this.changeQueryStatus(
        waitingQuery.queryId,
        Status.Error,
        message || "query status could not be retrieved",
      );
      return;
    }
    setTimeout(
      () => void this.pollWaitingQuery(waitingQuery),
      ChatStore.QUERY_POLLING_INTERVAL,
    );
  }

  private cancelRequest() {
    this.requestGeneration++;
    this.abortController.abort();
  }

  private async dryrunQuery(query: string) {
    const response = await call(
      Api.dryrunTitlesBigqueryQuery(
        this.parent.app.titleUuid,
        this.parent.app.env,
        { query },
      ),
      [400],
    );
    return response;
  }

  private getRailsCsrfToken(): string {
    if (document == null) {
      return "";
    }
    const meta = document.querySelector("meta[name=csrf-token]");
    return meta?.getAttribute("content") ?? "";
  }
}

interface PlotlyParams {
  data: unknown;
  layout?: unknown;
}

export class GraphFormStore extends StoreBase {
  readonly queryResult: QueryResult;
  readonly id: string;
  @observable public accessor script = `import matplotlib
import matplotlib.pyplot as plt
import matplotlib_fontja
import numpy as np
import pandas as pd
matplotlib.use("module://matplotlib_pyodide.html5_canvas_backend")
`;
  @observable public accessor isRunning = false;
  @observable public accessor runningStatus = "";
  @observable public accessor drawId = "";
  @observable public accessor success = false;
  @observable private accessor runError: Error | undefined = undefined;
  @observable public accessor title = "";
  @observable public accessor plotlyParams: PlotlyParams | undefined =
    undefined;
  private pyodideSession: PyodideSession | undefined;
  public readonly domId = `graph-id-${Math.random()}`;

  constructor(parent: Store, queryResult: QueryResult) {
    super(parent);
    this.id = `graphForm/${queryResult.queryId}`;
    this.title = I18n.t("bigquery_query_viewer.label.graph_s", {
      s: queryResult.queryId,
    });
    this.queryResult = queryResult;
  }

  @action public async run() {
    this.isRunning = true;
    this.drawId = `draw-id-${Math.random()}`;
    this.success = false;
    this.runError = undefined;
    using _ = defer(() => {
      this.isRunning = false;
      this.runningStatus = "";
    });
    if (!this.pyodideSession) {
      this.pyodideSession = await PyodideManager.startSession(
        ["matplotlib", "numpy", "pandas", "matplotlib-fontja", "plotly"],
        (status) => (this.runningStatus = status),
      );
    }
    this.runningStatus = "writing data.csv";
    this.pyodideSession.writeFile("/data.csv", this.queryResult.rawCsv ?? "");
    this.runningStatus = "running python script";
    (document as any).pyodideMplTarget = document.getElementById(this.domId);
    try {
      const result = await this.pyodideSession.run(this.script);
      this.plotlyParams = this.parsePlotlyParams(result);
      this.success = true;
    } catch (e) {
      if (e instanceof Error) {
        this.runError = e;
      }
    }
  }

  private parsePlotlyParams(result: unknown): PlotlyParams | undefined {
    if (typeof result != "string") {
      return;
    }
    try {
      const obj = JSON.parse(result);
      if ("data" in obj) {
        return obj;
      }
    } catch {
      return;
    }
  }

  @computed get markers() {
    if (!this.runError) {
      return [];
    }

    const match = this.runError.message.match(
      /File "<exec>", line (\d+), in <module>/,
    );
    let lineNumber = 1;
    if (match) {
      lineNumber = parseInt(match[1]);
    }
    let message = "";
    if ("type" in this.runError && typeof this.runError.type == "string") {
      const type = this.runError.type;
      const lines = this.runError.message.replace("\r\n", "\n").split("\n");
      const errorMatch = lines
        .map((line) => line.match(`${type}: (.*)$`))
        .find((_) => _);
      if (errorMatch) {
        message = errorMatch[1];
      }
    }
    return [
      {
        severity: monaco.MarkerSeverity.Error,
        startLineNumber: lineNumber,
        startColumn: 0,
        endLineNumber: lineNumber,
        endColumn: 0,
        message,
      },
    ];
  }
}

export interface ColumnField {
  name: string;
  id: string;
  children: ColumnField[];
}

export class QueryResult {
  private static readonly POLLING_INTERVAL = 20_000;

  @observable accessor rawCsv: string | undefined = undefined;
  @observable accessor csv: string[][] | undefined = undefined;
  @observable accessor csvHeader: string[] | undefined = undefined;
  @observable accessor csvBody: string[][] | undefined = undefined;
  @observable accessor isLoading = false;
  @observable accessor query = "";
  @observable accessor queryId = "";
  @observable accessor enqueuedAt: string | undefined = undefined;
  @observable accessor executionTime: number | undefined = undefined;
  @observable accessor cost: number | BigNumber | undefined = undefined;
  @observable accessor queryStatus = Status.WaitingEnqueue;
  @observable accessor queryMessage = "";
  @observable accessor exportStatus = Status.WaitingEnqueue;
  @observable accessor exportMessage = "";
  @observable accessor expandJson = false;
  @observable accessor columnFields: ColumnField[] = [];
  @observable accessor visibleColumns: string[] = [];
  private pollingTimer: ReturnType<typeof setTimeout> | undefined;
  private pollingGeneration = 0;
  private completionNotified = false;

  constructor(query: string, queryId: string) {
    this.query = query;
    this.queryId = queryId;
    this.enqueuedAt = new Date().toISOString();

    reaction(() => this.expandJson, this.parseCsv.bind(this));
  }

  static fromResponse(bigqueryQuery: BigqueryQuery) {
    const queryResult = new QueryResult(
      bigqueryQuery.query,
      bigqueryQuery.queryId,
    );
    runInAction(() => {
      queryResult.queryStatus = bigqueryQuery.status as Status;
      queryResult.enqueuedAt = bigqueryQuery.enqueuedAt;
      queryResult.executionTime = bigqueryQuery.executionTime;
      queryResult.queryMessage = bigqueryQuery.message ?? "";
      queryResult.cost = bigqueryQuery.cost;
    });
    return queryResult;
  }

  startPolling(titleUuid: string, env: string) {
    this.stopPolling();
    const pollingGeneration = this.pollingGeneration;
    this.pollingTimer = setTimeout(
      () => void this.pollQuery(titleUuid, env, pollingGeneration),
      QueryResult.POLLING_INTERVAL,
    );
  }

  stopPolling() {
    this.pollingGeneration++;
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = undefined;
    }
  }

  @action changeStatus(status: Status, message: string) {
    this.queryStatus = status;
    this.queryMessage = message;
    if (
      !this.completionNotified &&
      (status === Status.Success || status === Status.Error)
    ) {
      this.completionNotified = true;
      notifyQueryCompletion(this.queryId, status, message);
    }
  }

  private async pollQuery(
    titleUuid: string,
    env: string,
    pollingGeneration: number,
  ) {
    this.pollingTimer = undefined;
    try {
      const response = await call(
        Api.showTitlesBigqueryQuery(titleUuid, env, this.queryId),
      );
      if (pollingGeneration !== this.pollingGeneration) {
        return;
      }
      if (response.code !== 200) {
        this.startPolling(titleUuid, env);
        return;
      }

      const status = response.body.status as Status;
      runInAction(() => {
        this.changeStatus(status, response.body.message ?? "");
        this.executionTime = response.body.executionTime;
        this.cost = response.body.cost;
        if (status === Status.Success) {
          this.rawCsv = response.body.csv;
          this.parseCsv();
        }
      });

      if (
        status !== Status.Success &&
        status !== Status.Error &&
        status !== Status.Stopped
      ) {
        this.startPolling(titleUuid, env);
      }
    } catch {
      if (pollingGeneration === this.pollingGeneration) {
        this.startPolling(titleUuid, env);
      }
    }
  }

  @action async getQuery(titleUuid: string, env: string) {
    this.queryStatus = Status.Success;
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const response = await call(
      Api.showTitlesBigqueryQuery(titleUuid, env, this.queryId),
    );
    if (response.code != 200) {
      return;
    }
    runInAction(() => {
      this.rawCsv = response.body.csv;
      this.parseCsv();
      this.executionTime = response.body.executionTime;
      this.cost = response.body.cost;
    });
  }

  @action private parseCsv() {
    if (!this.rawCsv) {
      return;
    }
    if (this.expandJson) {
      this.csv = expandJsonInCsv(csvToArray(this.rawCsv));
    } else {
      this.csv = csvToArray(this.rawCsv);
    }
    this.csvHeader = this.csv[0];
    this.visibleColumns = this.csv[0].slice();
    this.csvBody = this.csv.slice(1);
    this.columnFields = this.generateColumnsFields(this.csv);
  }

  private generateColumnsFields(csv: string[][]): ColumnField[] {
    const columnFields: ColumnField[] = [];

    for (const column of csv[0]) {
      const splits = column.split(".");
      let currentColumnFields = columnFields;
      for (let i = 0; i < splits.length; i++) {
        const split = splits[i];
        const id = splits.slice(0, i + 1).join(".");
        let columnField = currentColumnFields.find((_) => _.id == id);
        if (!columnField) {
          columnField = {
            name: split,
            id: id,
            children: [],
          };
          currentColumnFields.push(columnField);
        }
        currentColumnFields = columnField.children;
      }
    }
    this.sortColumnFields(columnFields);
    return columnFields;
  }

  private sortColumnFields(columnFields: ColumnField[]) {
    columnFields.sort((x, y) => {
      if (x.children.length < y.children.length) {
        return -1;
      } else if (x.children.length > y.children.length) {
        return 1;
      }
      if (x.name < y.name) {
        return -1;
      } else if (x.name > y.name) {
        return 1;
      }
      for (let i = 0; i < x.children.length; i++) {
        if (x.children[i].name < y.children[i].name) {
          return -1;
        } else if (x.children[i].name > y.children[i].name) {
          return 1;
        }
      }
      return 0;
    });
    for (const columnField of columnFields) {
      if (columnField.children) {
        this.sortColumnFields(columnField.children);
      }
    }
  }

  @action async stopQuery(titleUuid: string, env: string) {
    this.queryStatus = Status.Stopping;
    const response = await call(
      Api.stopTitlesBigqueryQuery(titleUuid, env, this.queryId),
    );
    switch (response.code) {
      case 200:
        if (response.body.message != null) {
          notificationStore.notifySuccess(response.body.message);
        }
        this.queryStatus = Status.Stopped;
        break;
      default:
        this.queryStatus = Status.Running;
        break;
    }
  }

  @action async exportQuery(titleUuid: string, env: string) {
    this.exportStatus = Status.Waiting;
    const response = await call(
      Api.exportTitlesBigqueryQuery(titleUuid, env, this.queryId),
    );
    switch (response.code) {
      case 200:
        notificationStore.notifySuccess(response.body.message);
        this.exportStatus = Status.Success;
        break;
      default:
        this.exportStatus = Status.WaitingEnqueue;
        break;
    }
  }

  getCsv() {
    if (this.csv != null) {
      return arrayToCsv(this.csv);
    }
    return "";
  }
}
